import { AxiosRequestConfig } from 'axios';
import HttpClient from './HttpClient';
import HttpMethods from './HttpMethods';
import { ApiError, ResponsePagination } from './types';

interface HttpResponse<T = any> {
  data: T;
  error: ApiError;
  pagination: ResponsePagination;
  status: number;
}

export async function executeHttpRequest<T = any>(
  method: HttpMethods,
  url: string,
  config: AxiosRequestConfig,
  requestBody?: Record<string, any>
): Promise<HttpResponse<T>> {
  const httpFunctionForMethod = getFunctionForMethod(method);

  try {
    const response = await httpFunctionForMethod(
      url,
      config,
      requestBody || {}
    );

    return {
      data: response.data?.data || response.data,
      pagination: response.data?.pagination || null,
      error: null,
      status: response.status,
    };
  } catch (exception) {
    return {
      data: null,
      pagination: null,
      error: extractError(exception),
      status: exception.status || 400,
    };
  }
}

function getFunctionForMethod(requestMethod: HttpMethods) {
  switch (requestMethod) {
    case HttpMethods.DELETE:
      return HttpClient.delete;
    case HttpMethods.GET:
      return HttpClient.get;
    case HttpMethods.POST:
      return HttpClient.post;
    case HttpMethods.PUT:
      return HttpClient.put;
    case HttpMethods.PATCH:
      return HttpClient.patch;
    default:
      throw new Error(`Request Method '${requestMethod}' is not valid`);
  }
}

function extractError(error: any): ApiError {
  if (error == null) return { errorMessage: 'Something went wrong!' };

  if (error instanceof Error) return { errorMessage: error.message };

  if (typeof error === 'string') return { errorMessage: error };

  // Handle additionalInformation property due to SR Proxy error handling
  try {
    const additionalInfo = error.data?.additionalInformation;

    if (additionalInfo?.errorMessage) {
      const additionalErrorMessage = JSON.parse(additionalInfo.errorMessage);

      return {
        errorMessage:
          additionalErrorMessage?.errorMessage || 'Something went wrong!',
        errorType: additionalErrorMessage?.errorType,
      };
    }
    // eslint-disable-next-line no-empty
  } finally {
  }

  return {
    errorMessage: error.data?.errorMessage || 'Something went wrong!',
    errorType: error.data?.errorType,
  };
}
